import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Truck, Navigation, Activity, Clock, Gauge, TrendingUp,
  RefreshCw, MapPin, Calendar, Award, Zap
} from 'lucide-react';
import api from '../services/api';

// ─── Utilitários ─────────────────────────────────────────────────────────────
function fmtKm(km) {
  if (km == null) return '—';
  return `${Number(km).toFixed(1)} km`;
}
function fmtSpeed(s) {
  return s ? `${Number(s).toFixed(0)} km/h` : '—';
}
function fmtAgo(ts) {
  if (!ts) return '—';
  const t = (ts.endsWith('Z') || ts.includes('+')) ? ts : ts + 'Z';
  const diff = Math.floor((Date.now() - new Date(t)) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  return `${Math.floor(diff / 3600)}h`;
}

const STATUS_COLORS = {
  moving:  '#16a34a',
  stopped: '#ca8a04',
  offline: '#9ca3af',
};

// ─── Componentes de card ──────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon: Icon, color, bg }) {
  return (
    <div className={`${bg} rounded-xl p-4 flex items-center gap-4`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color} bg-white/60 dark:bg-white/10 flex-shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium truncate">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">{value}</p>
        {sub && <p className="text-xs text-gray-400 truncate">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Tooltip customizado ──────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 dark:text-white mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: <b>{Number(p.value).toFixed(1)} km</b>
        </p>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function FleetDashboardPage() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [sortBy, setSortBy]     = useState('km_month'); // km_today | km_week | km_month | max_speed

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/tracking/fleet-dashboard');
      setData(res.data.data);
      setLastUpdate(new Date());
    } catch (e) {
      console.error('Erro ao carregar fleet dashboard:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Carregando dashboard...
      </div>
    );
  }

  if (!data) return null;

  const { summary, vehicles } = data;

  // Dados para gráficos
  const sortedVehicles = [...vehicles].sort((a, b) => b[sortBy] - a[sortBy]);
  const top6 = sortedVehicles.slice(0, 6);

  const barData = top6.map(v => ({
    name: v.license_plate || v.imei?.slice(-6) || '—',
    'Hoje':  v.km_today,
    'Semana': v.km_week,
    'Mês':   v.km_month,
  }));

  const pieData = [
    { name: 'Em movimento', value: summary.moving,  color: STATUS_COLORS.moving  },
    { name: 'Parados',      value: summary.stopped, color: STATUS_COLORS.stopped },
    { name: 'Offline',      value: summary.offline, color: STATUS_COLORS.offline },
  ].filter(d => d.value > 0);

  const sortLabels = {
    km_today: 'Hoje', km_week: 'Semana', km_month: 'Mês', max_speed: 'Vel. Máx'
  };

  return (
    <div className="p-6 space-y-6 max-w-screen-2xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard da Frota</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {lastUpdate ? `Atualizado às ${lastUpdate.toLocaleTimeString('pt-BR')}` : ''}
          </p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard label="Total de Veículos" value={summary.total}
          icon={Truck} color="text-blue-600"
          bg="bg-blue-50 dark:bg-blue-900/20" />
        <KpiCard label="Em Movimento" value={summary.moving}
          icon={Navigation} color="text-green-600"
          bg="bg-green-50 dark:bg-green-900/20"
          sub={summary.total ? `${Math.round(summary.moving/summary.total*100)}% da frota` : ''} />
        <KpiCard label="Parados" value={summary.stopped}
          icon={Activity} color="text-yellow-600"
          bg="bg-yellow-50 dark:bg-yellow-900/20" />
        <KpiCard label="Offline" value={summary.offline}
          icon={Zap} color="text-gray-500"
          bg="bg-gray-100 dark:bg-gray-800" />
        <KpiCard label="Km Hoje (frota)" value={fmtKm(summary.total_km_today)}
          icon={MapPin} color="text-purple-600"
          bg="bg-purple-50 dark:bg-purple-900/20"
          sub={`${fmtKm(summary.total_km_week)} esta semana`} />
        <KpiCard label="Km no Mês" value={fmtKm(summary.total_km_month)}
          icon={Calendar} color="text-indigo-600"
          bg="bg-indigo-50 dark:bg-indigo-900/20"
          sub="últimos 30 dias" />
      </div>

      {/* ── Gráficos ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Bar chart km */}
        <div className="xl:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              Km por Veículo
            </h2>
            <div className="flex gap-1">
              {['km_today','km_week','km_month'].map(k => (
                <button key={k} onClick={() => setSortBy(k)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                    sortBy === k ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}>
                  {sortLabels[k]}
                </button>
              ))}
            </div>
          </div>
          {barData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Sem dados</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit=" km" />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Hoje"   fill="#93c5fd" radius={[3,3,0,0]} />
                <Bar dataKey="Semana" fill="#3b82f6" radius={[3,3,0,0]} />
                <Bar dataKey="Mês"    fill="#1d4ed8" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="flex items-center gap-4 mt-2 justify-center text-xs text-gray-400">
            {[['#93c5fd','Hoje'],['#3b82f6','Semana'],['#1d4ed8','Mês']].map(([c,l]) => (
              <span key={l} className="flex items-center gap-1">
                <span className="w-3 h-2 rounded-sm inline-block" style={{background:c}} />{l}
              </span>
            ))}
          </div>
        </div>

        {/* Pie status */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-green-500" />
            Status da Frota
          </h2>
          {pieData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Sem dados</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                  dataKey="value" paddingAngle={3}>
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} veículos`, n]} />
                <Legend iconType="circle" iconSize={8}
                  formatter={(v) => <span className="text-xs text-gray-600 dark:text-gray-300">{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          )}
          {/* Total no centro */}
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-1">
            {summary.total} veículo{summary.total !== 1 ? 's' : ''} rastreado{summary.total !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* ── Ranking ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Award className="w-4 h-4 text-yellow-500" />
            Ranking da Frota
          </h2>
          <div className="flex gap-1">
            {Object.entries(sortLabels).map(([k, l]) => (
              <button key={k} onClick={() => setSortBy(k)}
                className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                  sortBy === k ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50">
                {['#','Veículo','Status','Km Hoje','Km Semana','Km Mês','Vel. Máx','Vel. Média','Utilização','Motorista','Último sinal'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortedVehicles.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-12 text-gray-400">Nenhum veículo rastreado ainda</td></tr>
              ) : sortedVehicles.map((v, i) => (
                <tr key={v.vehicle_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                  <td className="px-4 py-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                      i === 0 ? 'bg-yellow-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-600' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-white'
                    }`}>{i + 1}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900 dark:text-white">{v.license_plate || '—'}</p>
                    <p className="text-xs text-gray-400">{v.brand} {v.model}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      v.is_offline  ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' :
                      v.is_moving   ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {v.is_offline ? 'Offline' : v.is_moving ? 'Em movimento' : 'Parado'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{fmtKm(v.km_today)}</td>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{fmtKm(v.km_week)}</td>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{fmtKm(v.km_month)}</td>
                  <td className="px-4 py-3 text-orange-600 font-medium">{fmtSpeed(v.max_speed)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{fmtSpeed(v.avg_speed)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 min-w-[60px]">
                        <div className="h-1.5 rounded-full bg-blue-500"
                          style={{ width: `${Math.min(v.utilization_pct, 100)}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-8">{v.utilization_pct}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{v.driver_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{fmtAgo(v.gps_timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
